<template>
  <slot name="activator" :on-click="open" />
  <Teleport to="body">
    <transition name="show-modal">
      <div
        v-show="isOpen"
        ref="modal"
        class="modal"
        @mousedown="onMouseDown"
        @mouseup.self="closing && close()"
      >
        <div
          :key="splitPanelsKey"
          class="modal__panels"
          :class="panelsWrapperClass"
        >
          <div
            v-if="isMobileStackedPanels"
            class="modal__content"
            :class="mobileStackedPanelClass"
          >
            <transition name="fade">
              <div
                v-if="showBackButton"
                class="modal__back"
                @click="emit('return-back')"
              >
                <svgo-arrow-left width="20" height="20" class="icon" />
              </div>
            </transition>
            <div class="modal__close" @click="handleMobileStackedClose">
              <svgo-close width="20" height="20" class="icon" />
            </div>

            <div class="modal__content-inner">
              <slot
                name="content"
                :close="close"
                :close-left-content="closeLeftContent"
              />
            </div>
          </div>

          <template v-else>
            <div
              v-if="showLeftContent"
              class="modal__content"
              :class="leftPanelClass"
            >
              <transition name="fade">
                <div
                  v-if="showBackButton"
                  class="modal__back"
                  @click="emit('return-back')"
                >
                  <svgo-arrow-left width="20" height="20" class="icon" />
                </div>
              </transition>
              <div
                v-if="!hasDualPanels"
                class="modal__close"
                @click="closeLeftContent"
              >
                <svgo-close width="20" height="20" class="icon" />
              </div>

              <div class="modal__content-inner">
                <slot
                  name="content"
                  :close="close"
                  :close-left-content="closeLeftContent"
                />
              </div>
            </div>

            <div
              v-if="showRightContent && hasDualPanels"
              class="modal__content modal__content--right"
              :class="rightPanelClass"
            >
              <transition name="fade">
                <div
                  v-if="showBackButton && !slots['content']"
                  class="modal__back"
                  @click="emit('return-back')"
                >
                  <svgo-arrow-left width="20" height="20" class="icon" />
                </div>
              </transition>
              <div class="modal__close" @click="close()">
                <svgo-close width="20" height="20" class="icon" />
              </div>
              <div class="modal__content-inner">
                <slot name="content-right" :close="close" />
              </div>
            </div>
          </template>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import { ref, watch } from 'vue';
import { isSafariBrowser } from '~/utils/isSafariBrowser';

const props = defineProps({
  showBackButton: {
    type: Boolean,
    default: false,
  },
  contentClass: {
    type: [String, Array, Object],
    default: '',
  },
  contentRightClass: {
    type: [String, Array, Object],
    default: '',
  },
  splitAnimation: {
    type: Boolean,
    default: false,
  },
  splitActive: {
    type: Boolean,
    default: false,
  },
  dualPanels: {
    type: Boolean,
    default: undefined,
  },
});

const emit = defineEmits(['open', 'close', 'return-back', 'panel-change']);

const valueModel = defineModel({
  type: Boolean,
  default: false,
});

const isMobile = inject('isMobile');
const isSafari = ref(false);
const splitPanelsKey = ref(0);

const slots = useSlots();
const modal = ref(null);
const isOpen = ref(false);
const showLeftContent = ref(true);
const showRightContent = ref(false);
const closing = ref(false);
const bodyPadding = ref(null);

const hasDualPanels = computed(() => {
  if (props.dualPanels !== undefined) {
    return props.dualPanels;
  }

  return !!slots['content-right'];
});

const isMobileStackedPanels = computed(
  () => isMobile.value && hasDualPanels.value,
);

const splitLayoutActive = computed(
  () => hasDualPanels.value && props.splitActive && !isMobile.value,
);

const mobileStackedPanelClass = computed(() => [
  ...(showLeftContent.value ? leftPanelClass.value : rightPanelClass.value),
  !showLeftContent.value && 'modal__content--right',
]);

const disableSplitAnimation = computed(
  () => isSafari.value || !props.splitAnimation,
);

const splitPanelState = computed(() => ({
  '--disable-split-animation': disableSplitAnimation.value,
}));

const panelsWrapperClass = computed(() => {
  if (!splitLayoutActive.value) {
    return '';
  }

  return ['modal-split__wrapper', splitPanelState.value];
});

const leftPanelClass = computed(() => [
  props.contentClass,
  splitLayoutActive.value && ['modal-split__left', splitPanelState.value],
]);

const rightPanelClass = computed(() => [
  props.contentRightClass,
  splitLayoutActive.value && ['modal-split__right', splitPanelState.value],
]);

watch(isOpen, (value) => {
  if (value) {
    disableScroll();
    valueModel.value = value;
  } else {
    enableScroll();
    valueModel.value = value;
  }
});

watch(isMobile, () => {
  nextTick(syncDualPanelVisibility);
});

watch(
  () => props.splitActive,
  (active, wasActive) => {
    if (active && !wasActive && props.splitAnimation && !isMobile.value) {
      splitPanelsKey.value += 1;
    }

    syncDualPanelVisibility();
  },
  { immediate: true },
);

watch(hasDualPanels, () => {
  nextTick(syncDualPanelVisibility);
});

onMounted(() => {
  isSafari.value = isSafariBrowser();
  nextTick(syncDualPanelVisibility);
});

function syncDualPanelVisibility() {
  if (!hasDualPanels.value) {
    showRightContent.value = !isMobile.value;
    return;
  }

  if (isMobile.value) {
    if (props.splitActive) {
      resetPanelLayout();
    } else {
      setPanelVisibility({ showLeft: true, showRight: false });
    }

    return;
  }

  setPanelVisibility({
    showLeft: true,
    showRight: props.splitActive,
  });
}

function open() {
  isOpen.value = true;
  showLeftContent.value = true;
  showRightContent.value = hasDualPanels.value ? false : !isMobile.value;

  nextTick(() => {
    syncDualPanelVisibility();
    emit('open', { padding: bodyPadding.value });
  });
}

function onMouseDown(e) {
  closing.value = e.target === modal.value;
}

function emitPanelChange() {
  emit('panel-change', {
    showLeft: showLeftContent.value,
    showRight: showRightContent.value,
  });
}

function handleMobileStackedClose() {
  if (showLeftContent.value) {
    closeLeftContent();
  } else {
    close();
  }
}

function closeLeftContent() {
  if (hasDualPanels.value) {
    showLeftContent.value = false;
    showRightContent.value = true;
    emitPanelChange();
  } else {
    close();
  }
}

function resetPanelLayout() {
  showLeftContent.value = true;
  showRightContent.value = false;
  emitPanelChange();
}

function setPanelVisibility({ showLeft = true, showRight } = {}) {
  showLeftContent.value = showLeft;

  if (showRight !== undefined) {
    showRightContent.value = showRight;
  }
}

function close() {
  isOpen.value = false;
  closing.value = false;
  enableScroll();
  emit('close', { padding: bodyPadding.value });
}

function disableScroll() {
  const body = document.querySelector('body');
  body.dataset.scroll = scrollY;

  const bodyDefaultWidth = document.body.clientWidth;
  body.style.top = `-${document.body.dataset.scroll}px`;
  body.style.position = 'fixed';

  const bodyDisableWidth = document.body.clientWidth;
  bodyPadding.value = bodyDisableWidth - bodyDefaultWidth;
  body.style.paddingRight = bodyPadding.value + 'px';
}

function enableScroll() {
  const body = document.querySelector('body');
  bodyPadding.value = null;
  body.style.top = null;
  body.style.position = null;
  body.style.paddingRight = null;

  window.scroll({
    top: body.dataset.scroll,
  });

  body.removeAttribute('data-scroll');
}

defineExpose({
  open,
  close,
  closeLeftContent,
  resetPanelLayout,
  setPanelVisibility,
  splitLayoutActive,
});
</script>

<style lang="scss">
@use '@/assets/styles/components/modal/index.scss' as *;
</style>
